/**
 * Accessible Menu Hover Behavior
 * - Desktop: Open dropdown on hover (title or arrow)
 * - Mobile: Open dropdown on click (arrow only) - native behavior
 * - Title always navigates on click
 */

class AccessibleMenuHover {
  constructor() {
    this.isDesktop = window.matchMedia('(min-width: 992px)').matches;
    this.menuItems = new Map(); // Track items and their handlers
    this.init();

    // Re-initialize on resize
    window.addEventListener('resize', this.debounce(() => {
      const wasDesktop = this.isDesktop;
      this.isDesktop = window.matchMedia('(min-width: 992px)').matches;

      // Only re-init if we switched between mobile/desktop
      if (wasDesktop !== this.isDesktop) {
        this.cleanup();
        this.init();
      }
    }, 250));
  }

  cleanup() {
    // Remove all event listeners
    this.menuItems.forEach((handlers, item) => {
      if (handlers.mouseenter) {
        item.removeEventListener('mouseenter', handlers.mouseenter);
        // Also remove from dropdown if it exists
        if (handlers.dropdown) {
          handlers.dropdown.removeEventListener('mouseenter', handlers.mouseenter);
        }
      }
      if (handlers.mouseleave) {
        item.removeEventListener('mouseleave', handlers.mouseleave);
        // Also remove from dropdown if it exists
        if (handlers.dropdown) {
          handlers.dropdown.removeEventListener('mouseleave', handlers.mouseleave);
        }
      }
      if (handlers.summaryClick) {
        const summary = item.querySelector('summary');
        if (summary) {
          summary.removeEventListener('click', handlers.summaryClick);
        }
      }
    });
    this.menuItems.clear();
  }

  init() {
    // Skip initialization entirely on mobile - let native behavior work
    if (!this.isDesktop) {
      return;
    }

    const menuItems = document.querySelectorAll('.menu-item-with-dropdown');

    menuItems.forEach(item => {
      const details = item.querySelector('details');
      const anchor = item.querySelector('a');

      if (!details || !anchor) return;

      this.setupDesktopMenuItem(item);
    });
  }

  setupDesktopMenuItem(item) {
    const details = item.querySelector('details');
    const summary = item.querySelector('summary');
    const anchor = summary ? summary.querySelector('a') : null;

    if (!details || !summary || !anchor) return;

    let timeoutId = null;

    // Prevent anchor click from toggling details
    anchor.addEventListener('click', (e) => {
      e.stopPropagation();
      // Anchor navigates normally
    });

    // Create handlers
    const handlers = {
      mouseenter: () => {
        // Clear any pending close timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        details.setAttribute('open', '');
      },
      mouseleave: () => {
        // Delay closing to allow mouse to move to dropdown
        timeoutId = setTimeout(() => {
          details.removeAttribute('open');
          timeoutId = null;
        }, 150);
      },
      summaryClick: (e) => {
        // If click was on anchor, let it navigate
        if (e.target.closest('a')) {
          return;
        }
        // Otherwise prevent toggle
        e.preventDefault();
      }
    };

    // Hover on summary opens dropdown
    summary.addEventListener('mouseenter', handlers.mouseenter);
    summary.addEventListener('mouseleave', handlers.mouseleave);

    // Also add listeners to the dropdown content itself
    const dropdown = details.querySelector('.header__submenu, [id^="HeaderMenu-MenuList"], [id^="link-"]');
    if (dropdown) {
      dropdown.addEventListener('mouseenter', handlers.mouseenter);
      dropdown.addEventListener('mouseleave', handlers.mouseleave);
    }

    summary.addEventListener('click', handlers.summaryClick);

    // Store handlers and dropdown reference for cleanup
    this.menuItems.set(item, { ...handlers, dropdown });
  }  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new AccessibleMenuHover();
  });
} else {
  new AccessibleMenuHover();
}

// Re-initialize on Shopify section reload
document.addEventListener('shopify:section:load', () => {
  new AccessibleMenuHover();
});
